import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { IMAGES } from "../../lib/data";

const line = { hidden: { y: "110%" }, show: (i) => ({ y: 0, transition: { duration: 1, delay: 0.35 + i * 0.12, ease: [0.16, 1, 0.3, 1] } }) };
const HEAD = [
  { text: "Nowoczesne strony", em: false },
  { text: "internetowe, AI i", em: false },
  { text: "automatyzacje, które", em: false },
];

export const Hero = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen bg-[#050505] text-white overflow-hidden flex items-center" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="" className="w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.55) 0%, rgba(5,5,5,0.2) 40%, rgba(5,5,5,0.95) 100%)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(5,5,5,0.9) 0%, transparent 55%)" }} />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-28">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="flex items-center gap-3 mb-8">
          <span className="h-px w-10 bg-white/40" />
          <span className="text-xs uppercase tracking-[0.24em]" style={{ color: "var(--gold)" }}>Strony internetowe • AI • Automatyzacja</span>
        </motion.div>

        <h1 className="text-[2.5rem] min-[480px]:text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] tracking-tightest leading-[0.92] font-semibold max-w-5xl">
          {HEAD.map((l, i) => (
            <span key={i} className="block overflow-hidden py-[0.04em]">
              <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">
                {l.em ? <span className="font-serif-italic font-normal" style={{ color: "var(--gold)" }}>{l.text}</span> : l.text}
              </motion.span>
            </span>
          ))}
          <span className="block overflow-hidden py-[0.04em]">
            <motion.span variants={line} custom={3} initial="hidden" animate="show" className="block">
              ułatwiają <span className="font-serif-italic font-normal">prowadzenie firmy.</span>
            </motion.span>
          </span>
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-8 text-lg md:text-xl text-white/60 max-w-xl leading-relaxed">
          Projektujemy strony, chatboty i proste automatyzacje, które pomagają sprawniej obsługiwać klientów, oszczędzać czas i rozwijać obecność firmy w internecie.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9 }} className="mt-10 flex flex-wrap gap-4">
          <button data-testid="hero-primary-cta" onClick={() => navigate("/contact")}
            className="group inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-7 py-4 transition-transform duration-300 hover:-translate-y-0.5">
            Umów bezpłatną konsultację <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </button>
          <button data-testid="hero-secondary-cta" onClick={() => navigate("/portfolio")}
            className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 transition-colors duration-300 hover:bg-white/5">
            Zobacz portfolio <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40">
        <span className="text-[10px] uppercase tracking-[0.3em]">Przewiń</span>
        <span className="h-10 w-px bg-gradient-to-b from-white/40 to-transparent" />
      </motion.div>
    </section>
  );
};
